import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { t } from '@/utils/i18n-helper';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useForgotPassword() {
    const formData = ref({
        email: ''
    });

    const loading = ref(false);
    const success = ref(false);
    const mensaje = ref(null);
    const captchaToken = ref(null);
    const sitekey = process.env.VUE_APP_CLOUDFLARE_SITE_KEY;

    const footer = computed(() => store.getters.getFooter);

    const isEmailValid = computed(() => EMAIL_PATTERN.test((formData.value.email || '').trim()));

    // The token is the single source of truth: verifying it sets it, expiry and
    // error clear it. No extra flag is needed to know whether it is usable.
    const canSubmit = computed(() => (
        isEmailValid.value &&
        !!captchaToken.value &&
        !loading.value
    ));

    const onCaptchaVerified = token => {
        captchaToken.value = token;
    };

    /**
     * Turnstile tokens are single-use and can expire while the user is still
     * typing. Both events must invalidate the stored token, otherwise
     * canSubmit would stay true and the form would submit a stale token.
     */
    const onCaptchaInvalid = () => {
        captchaToken.value = null;
    };

    /**
     * Clears the stored token. Called after every failed attempt, because the
     * backend consumes the token and it cannot be reused.
     *
     * Resetting the widget itself is the view's responsibility: this composable
     * must not know the public API of a visual component.
     */
    const clearCaptcha = () => {
        captchaToken.value = null;
    };

    const submit = async() => {
        if (!canSubmit.value) return false;

        loading.value = true;
        mensaje.value = null;

        const payload = {
            email: formData.value.email.trim(),
            filters: {
                tid: store.getters.getConfig && store.getters.getConfig.hash_teatro
            },
            captchaToken: captchaToken.value
        };

        try {
            const res = await store.dispatch(config.getVuexAlias('forgot-password'), payload);
            mensaje.value = res.data.message;
            success.value = true;
            return true;
        } catch (error) {
            // The backend message is already localized; when the request never
            // reached it, fall back to a translation key. The composable never
            // picks a language: it hands the user-visible text to i18n.
            mensaje.value = error && error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : t('login.error_envio');
            clearCaptcha();
            return false;
        } finally {
            loading.value = false;
        }
    };

    return {
        formData,
        loading,
        success,
        mensaje,
        captchaToken,
        sitekey,
        footer,
        isEmailValid,
        canSubmit,
        onCaptchaVerified,
        onCaptchaInvalid,
        clearCaptcha,
        submit
    };
}
